import type { ReactNode } from 'react'
import { useT } from '../i18n'

/**
 * Ein Formularfeld mit Beschriftung, Hinweis und Fehlermeldung.
 *
 * Der Grund für ein eigenes Bauteil ist nicht die Ersparnis an Zeilen, sondern
 * die Verdrahtung: Beschriftung, `aria-describedby` und `aria-invalid` müssen
 * zusammenpassen, sonst liest eine Sprachausgabe den Fehler nie vor. Einmal
 * richtig gemacht ist verlässlicher als zehnmal abgeschrieben.
 */
export function Field({
  id,
  label,
  wert,
  onWert,
  onBlur,
  fehler,
  hinweis,
  typ = 'text',
  autoComplete,
  pflicht = false,
  inputMode,
}: {
  id: string
  label: string
  wert: string
  onWert: (wert: string) => void
  onBlur: () => void
  /** Null, solange nichts zu meckern ist — oder das Feld noch unberührt. */
  fehler: string | null
  hinweis?: ReactNode
  typ?: 'text' | 'email' | 'tel'
  autoComplete?: string
  pflicht?: boolean
  inputMode?: 'text' | 'numeric' | 'email' | 'tel'
}) {
  const t = useT()
  const fehlerId = `${id}-fehler`
  const hinweisId = `${id}-hinweis`
  const beschrieben = [fehler ? fehlerId : null, hinweis ? hinweisId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <p className="feld">
      <label htmlFor={id}>
        {label}
        {/* Gekennzeichnet wird das Freiwillige, nicht das Pflichtfeld: An der
            Kasse ist fast alles Pflicht, und ein Stern an jeder Zeile ist
            keine Auskunft mehr. „(freiwillig)" ausgeschrieben statt als
            Zeichen — den Stern liest nicht jede Sprachausgabe vor. */}
        {!pflicht && <span className="feld-zusatz"> {t('feld.freiwillig')}</span>}
      </label>
      <input
        id={id}
        type={typ}
        value={wert}
        required={pflicht}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={fehler ? true : undefined}
        aria-describedby={beschrieben || undefined}
        onChange={(ereignis) => onWert(ereignis.target.value)}
        onBlur={onBlur}
      />
      {hinweis && (
        <span className="feld-hinweis" id={hinweisId}>
          {hinweis}
        </span>
      )}
      {fehler && (
        <span className="feld-fehler" id={fehlerId}>
          {fehler}
        </span>
      )}
    </p>
  )
}
